import { useEffect, useState } from "react";

/**
 * User preferences blob. Replaces the previous pattern of one
 * standalone localStorage key per setting (`ov.kpi.slots.v3`,
 * `ov.chart.metrics.v1`, `oneads.ads_manager.columns.v2.campaigns`,
 * ...). All UI preferences now live inside a single versioned
 * namespaced JSON document under `oneads.prefs`, so wiping state
 * is a one-key delete instead of a scavenger hunt through
 * DevTools.
 *
 * Scope: UI layout + column visibility + per-card configuration.
 *
 * NOT in the blob (by design):
 *   - Auth tokens (api.ts/auth.tsx) — read on every request, kept
 *     as flat strings to avoid JSON.parse on the hot path, plus
 *     security auditors expect tokens to be unambiguously located.
 *   - Sync banner state — timestamped per completed ASA sync and
 *     self-expires, so consolidating it would just move TTL logic
 *     into the prefs layer for no benefit.
 */

const PREFS_KEY = "oneads.prefs";
const PREFS_VERSION = 1;

type PrefsBlob = { v: number; data: Record<string, unknown> };

// Legacy keys that pre-existed before the consolidation. On first
// read we copy each one into the blob (if still present) and then
// delete the standalone key, so users don't lose their saved
// dashboard layout across the upgrade. Keep this list in sync with
// Overview.tsx useLocalStorage callsites — a typo here doesn't
// break the app, but it silently resets that specific setting on
// the next render.
const LEGACY_KEYS: readonly string[] = [
  "ov.kpis.v2",
  "ov.chart.metrics.v1",
  "ov.byapp.cols.v1",
  "ov.lb.top.v1",
  "ov.lb.under.v1",
  "ov.lb.scale.v1",
  "ov.scale.basis.v1",
  "ov.top.slots.v1",
  "ov.top.default.v1",
  // ColumnsModal keys follow the pattern
  // `oneads.ads_manager.columns.v2.{tab}` — we discover those
  // dynamically below since `tab` is user-generated.
];

function emptyBlob(): PrefsBlob {
  return { v: PREFS_VERSION, data: {} };
}

function readBlob(): PrefsBlob {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return emptyBlob();
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.v === "number" &&
      parsed.v === PREFS_VERSION &&
      typeof parsed.data === "object" &&
      parsed.data != null
    ) {
      return parsed as PrefsBlob;
    }
    // Malformed or wrong-version blob — start fresh. The legacy
    // migration below will repopulate from any standalone keys
    // that still exist.
    return emptyBlob();
  } catch {
    return emptyBlob();
  }
}

function writeBlob(blob: PrefsBlob): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(blob));
  } catch {
    // quota exceeded or similar — a swallowed write is preferable
    // to a runtime crash on a UI-preference path.
  }
}

// Run the legacy migration at most once per tab. Using a module-
// level flag keeps it idempotent: every useLocalStorage hook mount
// would otherwise re-scan.
let _legacyMigrationRan = false;
function migrateLegacyKeysOnce(): void {
  if (_legacyMigrationRan) return;
  _legacyMigrationRan = true;

  if (typeof localStorage === "undefined") return;
  const blob = readBlob();
  let mutated = false;

  const candidates: string[] = [...LEGACY_KEYS];
  // Scan for dynamic ColumnsModal keys — `tab` is user-data so
  // they can't be enumerated statically. Snapshot keys up front;
  // modifying localStorage while iterating is undefined-behaviour
  // across browsers.
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith("oneads.ads_manager.columns.v2.")) {
      candidates.push(k);
    }
  }

  for (const key of candidates) {
    if (key in blob.data) continue; // already migrated or user-set
    const raw = localStorage.getItem(key);
    if (raw == null) continue;
    try {
      blob.data[key] = JSON.parse(raw);
      mutated = true;
    } catch {
      // Not JSON — skip; don't try to store a raw string, the
      // hook contract is T = JSON-parsable. User will hit the
      // `initial` default on next render.
    }
  }

  if (mutated) {
    writeBlob(blob);
    // Delete the originals only once we know the copy succeeded,
    // so an interrupted migration (write failed above) doesn't
    // lose the data.
    for (const key of candidates) {
      if (key in blob.data) localStorage.removeItem(key);
    }
  }
}

export function useLocalStorage<T>(
  key: string,
  initial: T
): [T, (updater: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    migrateLegacyKeysOnce();
    const blob = readBlob();
    if (key in blob.data) {
      try {
        return blob.data[key] as T;
      } catch {
        /* fall through */
      }
    }
    return initial;
  });

  useEffect(() => {
    const blob = readBlob();
    blob.data[key] = value;
    writeBlob(blob);
  }, [key, value]);

  return [value, setValue];
}

/** Imperative read — for callers outside React components (e.g.
 *  the ColumnsModal helpers that load column state at render
 *  time without a hook). */
export function readPref<T>(key: string, fallback: T): T {
  migrateLegacyKeysOnce();
  const blob = readBlob();
  if (key in blob.data) return blob.data[key] as T;
  return fallback;
}

/** Imperative write — pair with readPref for non-hook contexts. */
export function writePref<T>(key: string, value: T): void {
  const blob = readBlob();
  blob.data[key] = value;
  writeBlob(blob);
}
